import { useState } from "react";
import { Head, useForm, router } from "@inertiajs/react";
import { UserPlus } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import FormField from "@/components/forms/FormField";
import SelectField from "@/components/forms/SelectField";
import DivisionField from "@/components/schools/classroom/DivisionField";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import { formatRoom } from "@/lib/classroom";

export default function Edit({ school, membership, classroom, division_options, available_teachers, invite_teacher_path, can_add_teachers, update_path, delete_path, back }) {
  const confirm = useConfirm();
  const { data, setData, patch, processing, errors } = useForm({
    name: classroom.name,
    grade: classroom.grade || "",
    division: classroom.division || "",
  });
  const displayName = formatRoom(classroom.name);

  const submit = (e) => {
    e.preventDefault();
    patch(update_path);
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: `Delete ${displayName}?`,
      body: "Its students, their activation codes and logged minutes are deleted too. This can't be undone.",
      confirmLabel: `Delete ${displayName}`,
      destructive: true,
    });
    if (ok) router.delete(delete_path);
  };

  const removeTeacher = async (teacher) => {
    const ok = await confirm({
      title: `Remove ${teacher.name} from ${displayName}?`,
      body: "They stay on your school's team and can be added back any time.",
      confirmLabel: "Remove",
    });
    if (ok) router.delete(teacher.remove_path, { preserveScroll: true });
  };

  return (
    <SchoolLayout school={school} membership={membership} back={back} title={`Edit ${displayName}`}>
      <Head title={`Edit ${displayName}`} />

      <div className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Classroom details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField name="name" label="Classroom name" required value={data.name} onChange={(e) => setData("name", e.target.value)} error={errors.name} help="The name families know, like “Room 12”." />
                <FormField name="grade" label="Grade" required placeholder="Grade 5" value={data.grade} onChange={(e) => setData("grade", e.target.value)} error={errors.grade} />
                <DivisionField value={data.division} onChange={(division) => setData("division", division)} error={errors.division} options={division_options} />
              </div>
              <div className="border-t border-border pt-5">
                <Button type="submit" disabled={processing}>{processing ? "Saving…" : "Save changes"}</Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Teachers</CardTitle>
            <CardDescription>Teachers log this class's reading, print its take-home sheets and hand out prizes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {classroom.teachers?.length > 0 ? (
              <ul className="space-y-2">
                {classroom.teachers.map((t) => (
                  <li key={t.id} className="flex items-center justify-between rounded-lg border border-border px-4 py-2">
                    <span className="text-sm font-medium text-foreground">{t.name}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeTeacher(t)}>Remove</Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No teacher yet.</p>
            )}

            {can_add_teachers ? (
              <TeacherActions
                invitePath={invite_teacher_path}
                available={(available_teachers || []).filter((at) => !classroom.teachers?.some((t) => t.id === at.id))}
              />
            ) : (
              <p className="text-sm text-muted-foreground">You can add teachers once PagePledge approves your campaign.</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle>Delete this classroom</CardTitle>
            <CardDescription>Deletes its students, their activation codes and logged minutes. Classrooms with donations can't be deleted.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" size="sm" onClick={onDelete}>Delete {displayName}</Button>
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
}

function TeacherActions({ invitePath, available }) {
  const [inviting, setInviting] = useState(false);
  const [chosen, setChosen] = useState("");
  const { data, setData, post, processing, reset, errors } = useForm({ email: "", name: "" });
  const teacher = available.find((t) => String(t.id) === chosen);

  const invite = (e) => {
    e.preventDefault();
    post(invitePath, { preserveScroll: true, onSuccess: () => { reset(); setInviting(false); } });
  };

  return (
    <div className="space-y-4 border-t border-border pt-4">
      {available.length > 0 && (
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <SelectField
              name="existing_teacher"
              label="Add a teacher from your school"
              value={chosen}
              onChange={(e) => setChosen(e.target.value)}
              placeholder="Choose a teacher…"
              options={available.map((t) => ({ value: String(t.id), label: t.name }))}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={!teacher}
            onClick={() => router.post(teacher.add_path, {}, { preserveScroll: true, onSuccess: () => setChosen("") })}
          >
            Add
          </Button>
        </div>
      )}

      {inviting ? (
        <form onSubmit={invite} className="space-y-3">
          <p className="text-sm font-medium text-foreground">Invite a new teacher</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField name="name" label="Name" placeholder="Ms. Garcia" value={data.name} onChange={(e) => setData("name", e.target.value)} autoFocus error={errors.name} />
            <FormField name="email" label="Email" type="email" required placeholder="garcia@school.ca" value={data.email} onChange={(e) => setData("email", e.target.value)} error={errors.email} />
          </div>
          <p className="text-xs text-muted-foreground">They'll get an email with a sign-in link. There's no password.</p>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={processing || !data.email}>
              <UserPlus className="h-4 w-4" />
              {processing ? "Sending…" : "Send invitation"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setInviting(false)}>Cancel</Button>
          </div>
        </form>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setInviting(true)}>
          <UserPlus className="h-4 w-4" />
          Invite a new teacher
        </Button>
      )}
    </div>
  );
}
